import { isEmptyString } from '@/utils/string';

export const DeliverySettings = {
  Normal: 'normal',
  SendToOne: 'send_to_one',
  Off: 'off',
} as const;
export type DeliverySettings = (typeof DeliverySettings)[keyof typeof DeliverySettings];

/** Per-identifier enablement state (mirrors the Ember `IdentifierState` enum). */
export const IdentifierState = {
  Disabled: 0,
  Enabled: 1,
  EnabledImmutable: 2,
} as const;
export type IdentifierState = (typeof IdentifierState)[keyof typeof IdentifierState];

/** The identifiers a workspace can use (mirrors the Ember `IdentifierKey` enum). */
export const IdentifierKey = {
  CioId: 'cio_id',
  Id: 'id',
  Email: 'email',
  Phone: 'phone',
} as const;
export type IdentifierKey = (typeof IdentifierKey)[keyof typeof IdentifierKey];

/** A workspace identifies people either by email only, or by multiple identifiers. */
export const IdentifierType = {
  Email: 'email',
  MultiIdentifier: 'multi',
} as const;
export type IdentifierType = (typeof IdentifierType)[keyof typeof IdentifierType];

/** Target configurations the self-serve identifier migration can move a workspace to. */
export const SelfServeMigrateTo = {
  IdOnly: 'id_only',
  IdMutable: 'id_mutable',
  MultiId: 'multi_id',
} as const;
export type SelfServeMigrateTo = (typeof SelfServeMigrateTo)[keyof typeof SelfServeMigrateTo];

/**
 * The value sent as the `Migrate to` analytics property when an identifier
 * migration is submitted. Email uses a `SelfServeMigrateTo`; phone uses
 * `phone_enable` / `phone_disable` (mirrors the Ember analytics).
 */
export type IdentifierMigrationTrackTarget = SelfServeMigrateTo | 'phone_enable' | 'phone_disable';

/**
 * Workspace enforcement mode for the `cio_email_tracking_consent` attribute. Off applies no consent
 * gate; opt-in tracks only explicitly consented people; opt-out tracks everyone except people who
 * explicitly opted out. Mirrors the Ember `OpenTrackingConsentMode` enum.
 */
export const OpenTrackingConsentMode = {
  Off: 'off',
  OptIn: 'opt_in',
  OptOut: 'opt_out',
} as const;
export type OpenTrackingConsentMode = (typeof OpenTrackingConsentMode)[keyof typeof OpenTrackingConsentMode];

/** The action sent to the generalized identifier-migration endpoint (`identifiers/migrate`). */
export type IdentifierMigrationAction = 'enable' | 'disable';

/**
 * The migration workflow's current step, mirroring the backend `Phase` values
 * (services `domains/environments/identifier_toggle`). Enable and disable share
 * this set; some phases only occur for one action.
 */
export type IdentifierMigrationPhase =
  | 'preflight'
  | 'pausing_ingress'
  | 'merging_duplicates'
  | 'writing_identifiers'
  | 'committing_config'
  | 'propagating_config'
  | 'deleting'
  | 'completed'
  | 'already_done'
  | 'needs_manual_merge'
  | 'invalid_data'
  | 'failed';

/** The status returned by `GET /v1/environments/:id/identifiers/migrate/:workflow_id`. */
export interface IdentifierMigrationStatus {
  phase: IdentifierMigrationPhase;
  /** A scrubbed, user-safe message present only on the `failed` phase. */
  error?: string;
}

/** Phases the workflow can settle on. Everything else means it's still running. */
const TERMINAL_MIGRATION_PHASES: readonly IdentifierMigrationPhase[] = [
  'completed',
  'already_done',
  'needs_manual_merge',
  'invalid_data',
  'failed',
];

/**
 * The deterministic per-(workspace, identifier) Temporal workflow ID, mirroring
 * the backend `WorkflowID` helper. Because it's deterministic, the UI can query
 * migration status without a handle from a prior POST.
 */
export function buildIdentifierToggleWorkflowId(environmentId: string | number, identifier: IdentifierKey): string {
  return `identifier-toggle-${environmentId}-${identifier}`;
}

export function isMigrationInProgress(phase: IdentifierMigrationPhase): boolean {
  return !TERMINAL_MIGRATION_PHASES.includes(phase);
}

export function isMigrationFailure(phase: IdentifierMigrationPhase): boolean {
  return phase === 'failed';
}

/**
 * Terminal, non-failure phases where the enable did not take effect and the
 * workspace owner has to act (fix duplicates / invalid values) before retrying.
 */
export function isMigrationNeedsAttention(phase: IdentifierMigrationPhase): boolean {
  return phase === 'needs_manual_merge' || phase === 'invalid_data';
}

/**
 * Infers which action is running from an action-unique phase. The shared phases
 * (preflight/committing/propagating and the terminal ones) return undefined, so
 * callers fall back to the current `canIdentifyByPhone` state for labeling.
 */
export function inferMigrationAction(phase: IdentifierMigrationPhase): IdentifierMigrationAction | undefined {
  if (
    phase === 'pausing_ingress' ||
    phase === 'merging_duplicates' ||
    phase === 'writing_identifiers' ||
    phase === 'needs_manual_merge' ||
    phase === 'invalid_data'
  ) {
    return 'enable';
  }

  if (phase === 'deleting') {
    return 'disable';
  }

  return undefined;
}

/** How duplicate profiles are merged when enabling email as an identifier. */
export const AutoMergeOption = {
  Manual: 'manual',
  Newest: 'keep_newest',
  Oldest: 'keep_oldest',
} as const;
export type AutoMergeOption = (typeof AutoMergeOption)[keyof typeof AutoMergeOption];

/** Identifier-state values (`IdentifierState`) keyed by identifier. */
export interface WorkspaceIdentifiers {
  cio_id: number;
  id: number;
  email: number;
  phone?: number;
}

/** Reference-rule settings (which identifiers people can be referenced by). */
export interface IdentifierSettings {
  identify_by_id: boolean;
}

/**
 * Identifier configuration read off a loaded environment, plus the migration in
 * progress. Mirrors the fields the Ember environment model derives its
 * identifier getters from.
 */
export interface EnvironmentIdentifierConfig {
  identifier_type: string;
  identifiers?: WorkspaceIdentifiers;
  identifier_settings: IdentifierSettings;
  identifiers_migration_in_progress: string;
}

/**
 * The derived identifier flags the Ember environment model exposes as computed
 * getters. Centralised here so the React identifiers UI reads the same booleans
 * the Ember template did rather than re-deriving them inline.
 */
export interface EnvironmentIdentifierFlags {
  isMultiIdentifier: boolean;
  canIdentifyByEmail: boolean;
  canIdentifyByPhone: boolean;
  canIdentifyById: boolean;
  isIdentifyingById: boolean;
  isMigrating: boolean;
  migratingToImmutableId: boolean;
  migratingToMutableId: boolean;
  migratingToMultiId: boolean;
}

/** An identifier is enabled when it's present and not explicitly disabled. */
export function isIdentifierEnabled(state: number | undefined): boolean {
  return state != null && state !== IdentifierState.Disabled;
}

/** A workspace identifies people by email alone when its type says so. */
export function isEmailIdentified(identifierType: string): boolean {
  return identifierType === IdentifierType.Email;
}

/**
 * The identifiers a workspace has enabled, excluding the internal `cio_id`.
 * This is the set the identifier UI treats as user-facing.
 */
export function enabledIdentifiers(identifiers: WorkspaceIdentifiers | undefined): IdentifierKey[] {
  return identifierKeys(identifiers).filter(
    (key) => key !== IdentifierKey.CioId && isIdentifierEnabled(identifiers?.[key]),
  );
}

/**
 * Every enabled identifier, `cio_id` included. The JavaScript-snippet copy
 * lists these, because a workspace can be identified by `cio_id` alone.
 */
export function allEnabledIdentifiers(identifiers: WorkspaceIdentifiers | undefined): IdentifierKey[] {
  return identifierKeys(identifiers).filter((key) => isIdentifierEnabled(identifiers?.[key]));
}

/**
 * The identifiers present on a workspace, in a fixed order.
 *
 * The order is load-bearing: the snippet copy lists these terms in prose, so it
 * has to stay stable. `IdentifierKey` is declared in the same order the wire
 * sends them, so the two agree.
 */
function identifierKeys(identifiers: WorkspaceIdentifiers | undefined): IdentifierKey[] {
  return Object.values(IdentifierKey).filter((key) => key in (identifiers ?? {}));
}

/**
 * Classify a workspace's identifier configuration for analytics — mirrors the
 * Ember `currentIdentifier` analytics transform.
 */
export function currentIdentifierConfigLabel(identifiers: WorkspaceIdentifiers): string {
  if (identifiers.cio_id === IdentifierState.EnabledImmutable && identifiers.email === IdentifierState.Enabled) {
    return 'multi-id';
  }
  if (identifiers.cio_id === IdentifierState.Disabled && identifiers.email === IdentifierState.Enabled) {
    return 'email-based';
  }
  return 'id-based (Classic)';
}

/**
 * Derive the environment's identifier flags from its raw config — the React
 * equivalent of the Ember environment model's `isMultiIdentifier`,
 * `canIdentifyBy*`, and `migratingTo*` computed getters.
 */
export function deriveEnvironmentIdentifierFlags(config: EnvironmentIdentifierConfig): EnvironmentIdentifierFlags {
  const identifiers = config.identifiers;
  const migration = config.identifiers_migration_in_progress;
  return {
    isMultiIdentifier: config.identifier_type === IdentifierType.MultiIdentifier,
    canIdentifyByEmail: isIdentifierEnabled(identifiers?.email),
    canIdentifyByPhone: isIdentifierEnabled(identifiers?.phone),
    canIdentifyById: isIdentifierEnabled(identifiers?.id),
    isIdentifyingById: config.identifier_settings.identify_by_id,
    // Match Ember's `@notEmpty`: a missing/null/empty value means no migration is running. A bare
    // `!== ''` would treat an omitted `identifiers_migration_in_progress` as an active migration
    // and wrongly lock the identifiers panel.
    isMigrating: !isEmptyString(migration),
    migratingToImmutableId: migration === SelfServeMigrateTo.IdOnly,
    migratingToMutableId: migration === SelfServeMigrateTo.IdMutable,
    migratingToMultiId: migration === SelfServeMigrateTo.MultiId,
  };
}

export interface MergedWorkspace {
  id: number;
  account_id: number;
  cio_workspace_id: number;
  cdp_workspace_id: number;
  cio_initialized: boolean;
  cdp_initialized: boolean;
  name: string;
  color: string;
  delivery_settings: DeliverySettings;
  delivery_email: string;
  email_open_tracking: boolean;
  identifier_type: string;
  identifiers?: WorkspaceIdentifiers;
  user_ids: string[];
}

// The object type shape is owned by the backend; use the generated model.
export type { ObjectType as WorkspaceObjectType } from '@customerio/hydra-ui-api';

export interface SampleAccountsApi {
  accounts: SampleAccountApi[];
}

export interface SampleAccountApi {
  environment_ids: number[];
  id: number;
}
